import { Link } from "react-router-dom";
import { LogoMarkLight } from "./LogoMark";
import { trackNewsletterSubscribed } from "../lib/analytics";

const footerLinks = {
  Product: [
    { label: "Builder", href: "/builder" },
    { label: "Templates", href: "/discover/templates" },
    { label: "Blog", href: "/blog" },
    { label: "Pricing", href: "/pricing" },
  ],
  Resources: [
    { label: "Best React Email Templates", href: "/blog/best-react-email-templates" },
    { label: "Build Templates with AI", href: "/blog/build-email-templates-with-ai" },
    { label: "React Email vs MJML", href: "/blog/react-email-vs-mjml" },
  ],
  Company: [
    { label: "About", href: "/about" },
    { label: "Privacy", href: "/legal/privacy" },
    { label: "Terms", href: "/legal/terms" },
  ],
  Connect: [
    { label: "Email us", href: "mailto:hello@reactemailbuilder.com" },
  ],
};

export function Footer() {
  return (
    <footer className="dark-surface relative overflow-hidden text-slate-300">
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/40 to-transparent" />
      <div aria-hidden="true" className="absolute inset-0 bg-grid-fade opacity-[0.08]" />
      <div aria-hidden="true" className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-sky-500/10 blur-[120px]" />
      <div aria-hidden="true" className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-indigo-500/10 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        {/* Newsletter capture */}
        <div className="mb-12 rounded-lg border border-white/10 bg-white/[0.04] px-6 py-7 backdrop-blur-sm sm:px-7 sm:py-8">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-300">
                Email dev tips
              </p>
              <h3 className="mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                Get React Email tips, new templates, and product updates.
              </h3>
              <p className="mt-3 max-w-lg text-sm leading-6 text-slate-400">
                No spam. Unsubscribe anytime. We share new template releases, email client compatibility tips, and builder feature drops.
              </p>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const emailInput = form.elements.namedItem("footer-email") as HTMLInputElement;
                const email = emailInput.value;
                const btn = form.querySelector("button[type=submit]") as HTMLButtonElement;
                const orig = btn.textContent ?? "Subscribe";

                if (!email) return;

                btn.textContent = "Subscribing...";
                btn.disabled = true;

                try {
                  trackNewsletterSubscribed({ source: "footer" });
                  const resp = await fetch("/api/newsletter/subscribe", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email, source: "footer" }),
                  });

                  const data = await resp.json().catch(() => null) as { message?: string; error?: string } | null;

                  if (resp.ok) {
                    emailInput.value = "";
                    btn.textContent = data?.message ?? "Subscribed ✓";
                  } else {
                    btn.textContent = data?.error ?? "Something went wrong";
                    setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 3000);
                    return;
                  }
                } catch {
                  btn.textContent = "Network error";
                  setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 3000);
                  return;
                }

                setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 3000);
              }}
              className="flex w-full flex-col gap-3 sm:flex-row"
            >
              <input
                type="email"
                name="footer-email"
                required
                data-testid="newsletter-email-input"
                aria-label="Email address for newsletter"
                placeholder="you@company.com"
                className="min-w-0 flex-1 rounded-md border border-white/15 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 backdrop-blur transition focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/30"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-btn"
                className="rounded-md bg-white px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {Object.entries(footerLinks).map(([heading, links]) => (
            <div key={heading}>
              <h3 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/90">
                {heading}
              </h3>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith("http") ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-white">
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.href} className="text-sm text-slate-400 transition hover:text-white">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-white transition hover:text-slate-300">
            <LogoMarkLight className="h-6 w-6" />
            React Email Builder
          </Link>
          <p className="text-sm text-slate-500">
            © 2026 React Email Builder. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}